"use client";

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  CONFIGURATOR_COORDINATE_SCALE,
  applicableConfiguratorFields,
  applicableConfiguratorSurfaces,
  artworkHeightPpm,
  clampArtworkPlacement,
  configurationSelectionIssues,
  createCenteredArtwork,
  emptyProductConfigurationSelection,
  isSafeConfiguratorUploadUrl,
  maximumFittingArtworkWidthPpm,
  moveArtworkWithKeyboard,
  normalizeSelectionForVariant,
  percentToPpm,
  ppmToPercent,
  type ClientArtworkSelection,
  type ClientConfiguratorScalar,
  type ClientProductConfigurationSelection,
  type PublicConfiguratorField,
  type PublicConfiguratorSurface,
  type PublicConfiguratorTemplate,
  type PublicProductConfigurator,
} from "./product-configurator-ui";
import styles from "./ProductConfigurator.module.scss";

export interface ProductConfiguratorQuoteRequest {
  productId: string;
  variantId: string;
  revisionId: string;
  selection: ClientProductConfigurationSelection;
}

export interface ProductConfiguratorQuoteResult {
  taxPrices?: import("./types").StorefrontTaxPrices;
  revisionId: string | null;
  revisionVersion?: number;
  definitionFingerprint?: string;
  configurationFingerprint?: string;
  canonicalSelection?: ClientProductConfigurationSelection;
  basePriceMinor: number;
  surchargeMinor: number;
  unitPriceMinor: number;
  priceBreakdown?: Array<{ source: string; key: string; amountMinor: number }>;
  shippingMetrics?: {
    weightGrams: number | null;
    lengthMm: number | null;
    widthMm: number | null;
    heightMm: number | null;
    volumeCubicMm: number | null;
  };
}

export interface ProductConfiguratorProps {
  configurator: PublicProductConfigurator | null;
  productId: string;
  variantId: string;
  locale?: string;
  currency?: string;
  value?: ClientProductConfigurationSelection;
  initialValue?: ClientProductConfigurationSelection;
  disabled?: boolean;
  quoteDelayMs?: number;
  className?: string;
  /** Optional storefront chrome translations; API-provided field/template labels remain authoritative. */
  uiLabels?: Partial<ProductConfiguratorUiLabels>;
  onChange?(selection: ClientProductConfigurationSelection): void;
  /** The caller owns the API request. Its server response is authoritative. */
  onQuote?(
    request: ProductConfiguratorQuoteRequest,
    signal: AbortSignal,
  ): Promise<ProductConfiguratorQuoteResult>;
  onResolvedQuote?(quote: ProductConfiguratorQuoteResult | null): void;
  onValidityChange?(valid: boolean): void;
}

export interface ProductConfiguratorUiLabels {
  quoteError: string;
  eyebrow: string;
  title: string;
  help: string;
  quotePending: string;
  configuredPrice: string;
  quoteAtCart: string;
  options: string;
  emptyChoice: string;
  printArea: string;
  dragInstruction: string;
  imageUnavailable: string;
  keyboardHelp: string;
  choosePrint: string;
  removePrint: string;
  width: string;
  layerLimit: string;
  artworkLimit: string;
  completeTitle: string;
  complete: string;
}

export const DUTCH_PRODUCT_CONFIGURATOR_LABELS: ProductConfiguratorUiLabels = {
  quoteError: "De prijs kon niet worden berekend. Controleer je keuzes en probeer opnieuw.",
  eyebrow: "Personaliseren",
  title: "Maak het product op maat",
  help: "Vul de opties in en plaats een opdruk binnen het aangegeven vlak.",
  quotePending: "Prijs berekenen…",
  configuredPrice: "Prijs met opties",
  quoteAtCart: "De definitieve prijs wordt veilig op de server berekend.",
  options: "Productopties",
  emptyChoice: "Maak een keuze",
  printArea: "Bedrukbaar vlak",
  dragInstruction: "versleep met de muis of gebruik de pijltoetsen",
  imageUnavailable: "De productafbeelding is niet beschikbaar. Kies deze optie nu niet.",
  keyboardHelp: "Selecteer een opdruk en verplaats deze met de pijltoetsen. Houd Shift ingedrukt voor grotere stappen.",
  choosePrint: "Kies een opdruk",
  removePrint: "Verwijderen",
  width: "Breedte",
  layerLimit: "Het maximale aantal opdrukken voor dit vlak is bereikt.",
  artworkLimit: "Een productconfiguratie kan maximaal 32 opdrukken bevatten.",
  completeTitle: "Maak de configuratie compleet",
  complete: "De productconfiguratie is compleet.",
};

type QuoteState =
  | { status: "idle" }
  | { status: "pending" }
  | { status: "resolved"; quote: ProductConfiguratorQuoteResult }
  | { status: "error"; message: string };

type DragState = {
  pointerId: number;
  surfaceId: string;
  layer: number;
  startX: number;
  startY: number;
  startArtwork: ClientArtworkSelection;
  width: number;
  height: number;
};

function money(valueMinor: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale || "nl-NL", { style: "currency", currency }).format(valueMinor / 100);
  } catch {
    return `${(valueMinor / 100).toFixed(2)} ${currency}`;
  }
}

function describedBy(id: string, field: PublicConfiguratorField): string | undefined {
  return field.helpText ? `${id}-help` : undefined;
}

function ConfiguratorFieldControl({
  field,
  value,
  disabled,
  id,
  emptyChoiceLabel,
  onChange,
}: {
  field: PublicConfiguratorField;
  value: ClientConfiguratorScalar | undefined;
  disabled: boolean;
  id: string;
  emptyChoiceLabel: string;
  onChange(value: ClientConfiguratorScalar | undefined): void;
}) {
  const common = {
    id,
    disabled,
    required: field.required,
    "aria-describedby": describedBy(id, field),
  };
  if (field.type === "short-text") {
    return <input {...common} type="text" maxLength={field.maximumLength ?? undefined} placeholder={field.placeholder} value={typeof value === "string" ? value : ""} onChange={(event) => onChange(event.target.value || undefined)} />;
  }
  if (field.type === "integer" || field.type === "measurement") {
    return <div className={`${styles.numberWithUnit} webshops-configurator__number-with-unit`}>
      <input
        {...common}
        type="number"
        inputMode="numeric"
        min={field.minimumScaled ?? undefined}
        max={field.maximumScaled ?? undefined}
        step={field.stepScaled ?? 1}
        value={typeof value === "number" ? value : ""}
        onChange={(event) => onChange(event.target.value === "" ? undefined : Number(event.target.value))}
      />
      <span aria-hidden="true">{field.canonicalUnit === "mm" ? "mm" : field.canonicalUnit === "gram" ? "g" : ""}</span>
    </div>;
  }
  if (field.type === "choice") {
    return <select {...common} value={typeof value === "string" ? value : ""} onChange={(event) => onChange(event.target.value || undefined)}>
      <option value="">{emptyChoiceLabel}</option>
      {field.choices.map((choice) => <option key={choice.id} value={choice.key}>{choice.label}</option>)}
    </select>;
  }
  return <label className={`${styles.checkboxControl} webshops-configurator__checkbox-control`}>
    <input id={id} disabled={disabled} type="checkbox" checked={value === true} onChange={(event) => onChange(event.target.checked)} aria-describedby={describedBy(id, field)} />
    <span>{field.label}</span>
  </label>;
}

function nextLayer(surface: PublicConfiguratorSurface, artworks: readonly ClientArtworkSelection[]): number | null {
  const used = new Set(artworks.filter((artwork) => artwork.surfaceId === surface.id).map((artwork) => artwork.layer));
  for (let layer = 0; layer < surface.maximumLayers; layer += 1) if (!used.has(layer)) return layer;
  return null;
}

export default function ProductConfigurator({
  configurator,
  productId,
  variantId,
  locale = "nl-NL",
  currency = "EUR",
  value,
  initialValue,
  disabled = false,
  quoteDelayMs = 250,
  className,
  uiLabels,
  onChange,
  onQuote,
  onResolvedQuote,
  onValidityChange,
}: ProductConfiguratorProps) {
  const ui = useMemo(() => ({ ...DUTCH_PRODUCT_CONFIGURATOR_LABELS, ...uiLabels }), [uiLabels]);
  const rootId = useId().replaceAll(":", "");
  const [internalValue, setInternalValue] = useState<ClientProductConfigurationSelection>(() => initialValue ?? emptyProductConfigurationSelection());
  const [quoteState, setQuoteState] = useState<QuoteState>({ status: "idle" });
  const drag = useRef<DragState | null>(null);
  const callbacks = useRef({ onChange, onQuote, onResolvedQuote, onValidityChange });
  callbacks.current = { onChange, onQuote, onResolvedQuote, onValidityChange };
  const quoteSequence = useRef(0);
  const hasQuoteCallback = Boolean(onQuote);
  const hasValidityCallback = Boolean(onValidityChange);

  const selection = useMemo(
    () => configurator ? normalizeSelectionForVariant(configurator, variantId, value ?? internalValue) : emptyProductConfigurationSelection(),
    [configurator, internalValue, value, variantId],
  );
  const fields = useMemo(() => configurator ? applicableConfiguratorFields(configurator, variantId) : [], [configurator, variantId]);
  const surfaces = useMemo(() => configurator ? applicableConfiguratorSurfaces(configurator, variantId) : [], [configurator, variantId]);
  const issues = useMemo(() => configurator ? configurationSelectionIssues(configurator, variantId, selection) : [], [configurator, selection, variantId]);
  const selectionJson = useMemo(() => JSON.stringify(selection), [selection]);
  const sourceSelectionJson = useMemo(() => JSON.stringify(value ?? internalValue), [internalValue, value]);

  const commit = (next: ClientProductConfigurationSelection) => {
    const normalized = configurator ? normalizeSelectionForVariant(configurator, variantId, next) : next;
    // Invalidate synchronously with the user's edit; the previous quote must
    // never remain actionable during the debounce before the next request.
    quoteSequence.current += 1;
    setQuoteState({ status: "idle" });
    callbacks.current.onResolvedQuote?.(null);
    if (value === undefined) setInternalValue(normalized);
    onChange?.(normalized);
  };

  useEffect(() => {
    callbacks.current.onValidityChange?.(Boolean(configurator) && issues.length === 0);
  }, [configurator, hasValidityCallback, issues.length]);

  useEffect(() => {
    if (!configurator || sourceSelectionJson === selectionJson) return;
    if (value === undefined) setInternalValue(selection);
    callbacks.current.onChange?.(selection);
  }, [configurator, selection, selectionJson, sourceSelectionJson, value]);

  useEffect(() => {
    const quote = callbacks.current.onQuote;
    if (!configurator || !quote || issues.length > 0 || disabled) {
      setQuoteState({ status: "idle" });
      callbacks.current.onResolvedQuote?.(null);
      return;
    }
    // A quote belongs to one exact semantic selection. Invalidate it before
    // the debounce window so no caller can submit or display a stale price.
    setQuoteState({ status: "idle" });
    callbacks.current.onResolvedQuote?.(null);
    const controller = new AbortController();
    const sequence = ++quoteSequence.current;
    const timer = window.setTimeout(() => {
      setQuoteState({ status: "pending" });
      void quote({ productId, variantId, revisionId: configurator.revisionId, selection }, controller.signal)
        .then((result) => {
          if (controller.signal.aborted || sequence !== quoteSequence.current) return;
          if (
            result.revisionId !== configurator.revisionId ||
            !Number.isSafeInteger(result.basePriceMinor) || result.basePriceMinor < 0 ||
            !Number.isSafeInteger(result.surchargeMinor) || result.surchargeMinor < 0 ||
            !Number.isSafeInteger(result.unitPriceMinor) || result.unitPriceMinor < 0 ||
            !Number.isSafeInteger(result.basePriceMinor + result.surchargeMinor) ||
            result.basePriceMinor + result.surchargeMinor !== result.unitPriceMinor
          ) {
            throw new Error("Ongeldige prijsopgave");
          }
          setQuoteState({ status: "resolved", quote: result });
          callbacks.current.onResolvedQuote?.(result);
        })
        .catch(() => {
          if (controller.signal.aborted || sequence !== quoteSequence.current) return;
          setQuoteState({ status: "error", message: ui.quoteError });
          callbacks.current.onResolvedQuote?.(null);
        });
    }, Math.min(5_000, Math.max(0, Number.isFinite(quoteDelayMs) ? quoteDelayMs : 250)));
    return () => { window.clearTimeout(timer); controller.abort(); };
    // selectionJson intentionally makes only semantic selection changes requote.
  }, [configurator?.revisionId, disabled, hasQuoteCallback, issues.length, productId, quoteDelayMs, selectionJson, ui.quoteError, variantId]);

  if (!configurator) return null;

  const updateField = (key: string, nextValue: ClientConfiguratorScalar | undefined) => {
    const nextFields = { ...selection.fields };
    if (nextValue === undefined) delete nextFields[key];
    else nextFields[key] = nextValue;
    commit({ ...selection, fields: nextFields });
  };

  const updateArtwork = (surfaceId: string, layer: number, nextArtwork: ClientArtworkSelection) => {
    const surface = surfaces.find((candidate) => candidate.id === surfaceId);
    const template = surface?.templates.find((candidate) => candidate.id === nextArtwork.templateId);
    if (!surface || !template) return;
    const normalized = clampArtworkPlacement(nextArtwork, template);
    if (!normalized || normalized.layer >= surface.maximumLayers) return;
    commit({
      ...selection,
      artworks: selection.artworks.map((artwork) => artwork.surfaceId === surfaceId && artwork.layer === layer ? normalized : artwork),
    });
  };

  const addTemplate = (surface: PublicConfiguratorSurface, template: PublicConfiguratorTemplate) => {
    if (!isSafeConfiguratorUploadUrl(template.imageUrl)) return;
    const surfaceArtworks = selection.artworks.filter((artwork) => artwork.surfaceId === surface.id);
    const layer = surface.maximumLayers === 1 ? 0 : nextLayer(surface, selection.artworks);
    if (layer === null) return;
    const artwork = createCenteredArtwork(surface, template, layer);
    if (!artwork) return;
    commit({
      ...selection,
      artworks: [...selection.artworks.filter((candidate) => !(surface.maximumLayers === 1 && candidate.surfaceId === surface.id)), artwork],
    });
  };

  const removeArtwork = (surfaceId: string, layer: number) => {
    commit({ ...selection, artworks: selection.artworks.filter((artwork) => artwork.surfaceId !== surfaceId || artwork.layer !== layer) });
  };

  const pointerDown = (event: ReactPointerEvent<HTMLButtonElement>, artwork: ClientArtworkSelection) => {
    if (disabled || event.button !== 0) return;
    const parent = event.currentTarget.parentElement;
    if (!parent) return;
    const bounds = parent.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = {
      pointerId: event.pointerId,
      surfaceId: artwork.surfaceId,
      layer: artwork.layer,
      startX: event.clientX,
      startY: event.clientY,
      startArtwork: artwork,
      width: bounds.width,
      height: bounds.height,
    };
  };

  const pointerMove = (event: ReactPointerEvent<HTMLButtonElement>, template: PublicConfiguratorTemplate) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    event.preventDefault();
    const next = clampArtworkPlacement({
      ...current.startArtwork,
      xPpm: current.startArtwork.xPpm + Math.round(((event.clientX - current.startX) / current.width) * CONFIGURATOR_COORDINATE_SCALE),
      yPpm: current.startArtwork.yPpm + Math.round(((event.clientY - current.startY) / current.height) * CONFIGURATOR_COORDINATE_SCALE),
    }, template);
    if (next) updateArtwork(current.surfaceId, current.layer, next);
  };

  const endPointer = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (drag.current?.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    drag.current = null;
  };

  const rootClassName = ["webshops-configurator", styles.root, className].filter(Boolean).join(" ");
  return <section className={rootClassName} aria-labelledby={`${rootId}-title`} aria-busy={quoteState.status === "pending" || undefined}>
    <header className={`${styles.header} webshops-configurator__header`}>
      <div>
        <p className={`${styles.eyebrow} webshops-configurator__eyebrow`}>{ui.eyebrow}</p>
        <h2 id={`${rootId}-title`}>{ui.title}</h2>
        <p>{ui.help}</p>
      </div>
      <div className={`${styles.quote} webshops-configurator__quote`} aria-live="polite">
        {quoteState.status === "pending" ? ui.quotePending : null}
        {quoteState.status === "resolved" ? <><span>{ui.configuredPrice}</span><strong>{money(quoteState.quote.unitPriceMinor, currency, locale)}</strong></> : null}
        {quoteState.status === "error" ? <span className={`${styles.error} webshops-configurator__error`}>{quoteState.message}</span> : null}
        {quoteState.status === "idle" && !hasQuoteCallback ? ui.quoteAtCart : null}
      </div>
    </header>

    {fields.length > 0 ? <fieldset className={`${styles.fields} webshops-configurator__fields`} disabled={disabled}>
      <legend>{ui.options}</legend>
      <div className={`${styles.fieldGrid} webshops-configurator__field-grid`}>
        {fields.map((field) => {
          const id = `${rootId}-field-${field.id}`;
          return <div className={`${styles.field} webshops-configurator__field`} key={field.id} data-field={field.key} data-field-type={field.type}>
            {field.type !== "boolean" ? <label htmlFor={id}>{field.label}{field.required ? <span className={`${styles.required} webshops-configurator__required`} aria-hidden="true"> *</span> : null}</label> : null}
            <ConfiguratorFieldControl field={field} value={selection.fields[field.key]} disabled={disabled} id={id} emptyChoiceLabel={ui.emptyChoice} onChange={(next) => updateField(field.key, next)} />
            {field.helpText ? <small id={`${id}-help`}>{field.helpText}</small> : null}
            {field.type === "choice" && typeof selection.fields[field.key] === "string" ? <small>{field.choices.find((choice) => choice.key === selection.fields[field.key])?.description}</small> : null}
          </div>;
        })}
      </div>
    </fieldset> : null}

    {surfaces.map((surface) => {
      const surfaceArtworks = selection.artworks.filter((artwork) => artwork.surfaceId === surface.id).sort((left, right) => left.layer - right.layer);
      const safeBase = isSafeConfiguratorUploadUrl(surface.imageUrl);
      const surfaceAtLimit = surfaceArtworks.length >= surface.maximumLayers;
      const globalAtLimit = selection.artworks.length >= 32;
      return <fieldset className={`${styles.surfaceSection} webshops-configurator__surface-section`} disabled={disabled} key={surface.id}>
        <legend>{surface.label}{surface.required ? <span className={`${styles.required} webshops-configurator__required`} aria-hidden="true"> *</span> : null}</legend>
        {surface.helpText ? <p className={`${styles.help} webshops-configurator__help`}>{surface.helpText}</p> : null}
        <div className={`${styles.customizerLayout} webshops-configurator__customizer-layout`}>
          <div>
            {safeBase ? <div className={`${styles.canvas} webshops-configurator__canvas`}>
              <img src={surface.imageUrl} alt={surface.label} />
              <div
                className={`${styles.printSurface} webshops-configurator__print-surface`}
                style={{
                  left: `${ppmToPercent(surface.xPpm)}%`,
                  top: `${ppmToPercent(surface.yPpm)}%`,
                  width: `${ppmToPercent(surface.widthPpm)}%`,
                  height: `${ppmToPercent(surface.heightPpm)}%`,
                }}
                aria-label={ui.printArea}
              >
                {surfaceArtworks.map((artwork) => {
                  const template = surface.templates.find((candidate) => candidate.id === artwork.templateId);
                  if (!template || !isSafeConfiguratorUploadUrl(template.imageUrl)) return null;
                  const height = artworkHeightPpm(artwork.widthPpm, template);
                  return <button
                    className={`${styles.artwork} webshops-configurator__artwork`}
                    key={`${surface.id}-${artwork.layer}`}
                    type="button"
                    disabled={disabled}
                    style={{
                      left: `${ppmToPercent(artwork.xPpm)}%`,
                      top: `${ppmToPercent(artwork.yPpm)}%`,
                      width: `${ppmToPercent(artwork.widthPpm)}%`,
                      height: `${ppmToPercent(height)}%`,
                      zIndex: artwork.layer + 1,
                    }}
                    aria-label={`${template.label}, ${ui.dragInstruction}`}
                    onPointerDown={(event) => pointerDown(event, artwork)}
                    onPointerMove={(event) => pointerMove(event, template)}
                    onPointerUp={endPointer}
                    onPointerCancel={endPointer}
                    onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
                      const next = moveArtworkWithKeyboard(artwork, template, event.key, event.shiftKey);
                      if (!next) return;
                      event.preventDefault();
                      updateArtwork(surface.id, artwork.layer, next);
                    }}
                  ><img src={template.imageUrl} alt="" draggable={false} /></button>;
                })}
              </div>
            </div> : <p className={`${styles.error} webshops-configurator__error`} role="alert">{ui.imageUnavailable}</p>}
            <p className={`${styles.keyboardHint} webshops-configurator__keyboard-hint`}>{ui.keyboardHelp}</p>
          </div>

          <div className={`${styles.surfaceControls} webshops-configurator__surface-controls`}>
            <h3>{ui.choosePrint}</h3>
            <div className={`${styles.templateGrid} webshops-configurator__template-grid`}>
              {surface.templates.map((template) => {
                const safeTemplate = isSafeConfiguratorUploadUrl(template.imageUrl);
                return <button
                  className={`${styles.template} webshops-configurator__template`}
                  type="button"
                  key={template.id}
                  disabled={disabled || !safeTemplate || (surfaceAtLimit && surface.maximumLayers > 1) || (globalAtLimit && !(surface.maximumLayers === 1 && surfaceArtworks.length === 1))}
                  onClick={() => addTemplate(surface, template)}
                >
                  {safeTemplate ? <img src={template.imageUrl} alt="" /> : <span className={`${styles.imagePlaceholder} webshops-configurator__image-placeholder`} aria-hidden="true" />}
                  <span><strong>{template.label}</strong>{template.description ? <small>{template.description}</small> : null}{template.surchargeMinor > 0 ? <small>+ {money(template.surchargeMinor, currency, locale)}</small> : null}</span>
                </button>;
              })}
            </div>

            {surfaceArtworks.map((artwork) => {
              const template = surface.templates.find((candidate) => candidate.id === artwork.templateId);
              if (!template) return null;
              const maximumWidth = maximumFittingArtworkWidthPpm(template);
              const changeGeometry = (part: "xPpm" | "yPpm" | "widthPpm", event: ChangeEvent<HTMLInputElement>) => {
                updateArtwork(surface.id, artwork.layer, { ...artwork, [part]: percentToPpm(Number(event.target.value)) });
              };
              return <section className={`${styles.layerControls} webshops-configurator__layer-controls`} key={`${surface.id}-controls-${artwork.layer}`} aria-labelledby={`${rootId}-${surface.id}-${artwork.layer}-label`}>
                <div className={`${styles.layerHeader} webshops-configurator__layer-header`}>
                  <h4 id={`${rootId}-${surface.id}-${artwork.layer}-label`}>{template.label}{surface.maximumLayers > 1 ? ` — laag ${artwork.layer + 1}` : ""}</h4>
                  <button type="button" disabled={disabled} onClick={() => removeArtwork(surface.id, artwork.layer)}>{ui.removePrint}</button>
                </div>
                <div className={`${styles.geometryGrid} webshops-configurator__geometry-grid`}>
                  <label><span>X (%)</span><input type="number" min="0" max={ppmToPercent(CONFIGURATOR_COORDINATE_SCALE - artwork.widthPpm)} step="1" value={ppmToPercent(artwork.xPpm)} onChange={(event) => changeGeometry("xPpm", event)} /></label>
                  <label><span>Y (%)</span><input type="number" min="0" max={ppmToPercent(CONFIGURATOR_COORDINATE_SCALE - artworkHeightPpm(artwork.widthPpm, template))} step="1" value={ppmToPercent(artwork.yPpm)} onChange={(event) => changeGeometry("yPpm", event)} /></label>
                  <label><span>{ui.width} (%)</span><input type="number" min={ppmToPercent(template.minimumWidthPpm)} max={ppmToPercent(maximumWidth)} step="1" value={ppmToPercent(artwork.widthPpm)} onChange={(event) => changeGeometry("widthPpm", event)} /></label>
                </div>
              </section>;
            })}
            {surfaceAtLimit && surface.maximumLayers > 1 ? <p className={`${styles.help} webshops-configurator__help`}>{ui.layerLimit}</p> : null}
            {globalAtLimit ? <p className={`${styles.help} webshops-configurator__help`}>{ui.artworkLimit}</p> : null}
          </div>
        </div>
      </fieldset>;
    })}

    <div className={`${styles.validation} webshops-configurator__validation`} aria-live="polite">
      {issues.length > 0 ? <div className={`${styles.notice} webshops-configurator__notice`} role="alert"><strong>{ui.completeTitle}</strong><ul>{issues.slice(0, 6).map((issue) => <li key={`${issue.path}-${issue.message}`}>{issue.message}</li>)}</ul></div> : <p className={`${styles.success} webshops-configurator__success`}>{ui.complete}</p>}
    </div>
  </section>;
}
